import { BodyText, Card, Heading, Icon, IconSet } from '@postscript/components';
import { FlowWizardOptions } from 'components/flowBuilder/constants/trackingEvents';
import {
  FlowType,
  FlowWizardOptionsType,
  SubmitCreateFlowArgs,
} from 'components/flowBuilder/types';
import { useCallback } from 'react';
import styled from 'styled-components';
import {
  COMMON_EVENT_NAMES,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';
import { InfinityBillingState } from '../hooks/useITCUpsellExperiment';
import MoreOptionsCard from './MoreOptionsCard';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  min-width: 640px;
`;

const StyledProminentCard = styled(Card)`
  border: 1px solid var(--border-color-dim);
  border-radius: var(--border-radius-medium);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  transition: var(--hover-transition);

  &:hover,
  &:focus {
    border-color: var(--highlight-color);
  }

  &:hover .prominent-arrow,
  &:focus .prominent-arrow {
    color: var(--highlight-color);
  }
`;

const StyledProminentHeader = styled.div`
  align-items: center;
  display: flex;
  gap: var(--spacing-2);
  padding: var(--spacing-3);
`;

const StyledProminentTextWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--spacing-1);
`;

const StyledProminentArrow = styled(Icon)`
  margin-left: auto;
`;

const StyledCallout = styled.div`
  align-items: center;
  background-color: rgba(203, 248, 211, 0.2);
  border-top: 1px solid var(--border-color-dim);
  display: flex;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-3);
`;

const StyledMoreOptionsGrid = styled.div`
  display: grid;
  gap: var(--spacing-3);
  grid-template-columns: repeat(3, minmax(0, 1fr));
`;

interface CreateFlowGridExperimentProps {
  flowType: FlowType;
  handleSubmit: (args?: SubmitCreateFlowArgs) => void;
  onChooseTemplate: () => void;
  showBasicCampaignOption: boolean;
  showCampaignTemplateOption: boolean;
  showLegacyCampaignOption: boolean;
  experimentEligible: boolean;
  infinityBillingState: InfinityBillingState;
}

export default function CreateFlowGridExperiment({
  flowType,
  handleSubmit,
  onChooseTemplate,
  showBasicCampaignOption,
  showCampaignTemplateOption,
  showLegacyCampaignOption,
  experimentEligible,
  infinityBillingState,
}: CreateFlowGridExperimentProps) {
  const trackAndDispatch = useCallback(
    (
      type: FlowWizardOptionsType,
      cardPosition: 'prominent' | 'more_options',
    ) => {
      logEvent(COMMON_EVENT_NAMES.MODAL_CTA_CLICKED, {
        flow_type: flowType,
        modal_type: MODAL_TYPES.CREATE_CAMPAIGN,
        modal_selection_value: type,
        product_area: PRODUCT_AREAS.FLOW_BUILDER,
        experiment_variant: 'treatment',
        experiment_eligible: experimentEligible,
        infinity_billing_state: infinityBillingState,
        card_position: cardPosition,
      });

      if (type === FlowWizardOptions.CAMPAIGN_TEMPLATE) {
        onChooseTemplate();
        return;
      }
      handleSubmit({ from: type });
    },
    [
      flowType,
      handleSubmit,
      onChooseTemplate,
      experimentEligible,
      infinityBillingState,
    ],
  );

  return (
    <StyledWrapper>
      <StyledProminentCard
        data-cy="create-infinity-test-campaign-card"
        data-testid="create-infinity-test-campaign-card"
        onClick={() =>
          trackAndDispatch(
            FlowWizardOptions.INFINITY_TEST_CAMPAIGN,
            'prominent',
          )
        }
        size="small"
      >
        <StyledProminentHeader>
          <Icon
            component={IconSet.Sparkles}
            size={36}
            color="var(--highlight-color)"
          />
          <StyledProminentTextWrapper>
            <Heading as="h4" size="xx-small">
              AI Infinity Test Campaign
            </Heading>
            <BodyText color="var(--text-color-dim)" size="small">
              Continuously test AI message variants to maximize EPM or UCTR.
            </BodyText>
          </StyledProminentTextWrapper>
          <StyledProminentArrow
            className="prominent-arrow"
            component={IconSet.ArrowRight}
            color="var(--border-color-dim)"
            size="large"
          />
        </StyledProminentHeader>
        <StyledCallout>
          <Icon
            component={IconSet.ArrowUpRight}
            size={16}
            color="var(--banner-success-icon-color)"
          />
          <BodyText size="small">
            Infinity Testing campaigns average <strong>40% higher EPM</strong>{' '}
            than traditional flows.
          </BodyText>
        </StyledCallout>
      </StyledProminentCard>
      <StyledMoreOptionsGrid>
        {showBasicCampaignOption && (
          <MoreOptionsCard
            testId="create-basic-campaign-card"
            description="Send a single-message campaign to a segment."
            icon={IconSet.Announcement}
            onClick={() =>
              trackAndDispatch(FlowWizardOptions.BASIC_CAMPAIGN, 'more_options')
            }
            title="Basic Campaign"
          />
        )}
        {showLegacyCampaignOption && (
          <MoreOptionsCard
            testId="create-legacy-campaign-card"
            description="Send a single-message campaign to a segment."
            icon={IconSet.Announcement}
            onClick={() =>
              trackAndDispatch(
                FlowWizardOptions.LEGACY_CAMPAIGN,
                'more_options',
              )
            }
            title="Standard Campaign"
          />
        )}
        <MoreOptionsCard
          testId="create-flow-campaign-card"
          description="Build a campaign flow from a blank canvas."
          icon={IconSet.Split}
          onClick={() =>
            trackAndDispatch(FlowWizardOptions.CAMPAIGN_FLOW, 'more_options')
          }
          title="Campaign Flow"
        />
        {showCampaignTemplateOption && (
          <MoreOptionsCard
            testId="choose-template-card"
            description="Need some inspiration? Start with a prebuilt campaign."
            icon={IconSet.GridMasonry}
            onClick={() =>
              trackAndDispatch(
                FlowWizardOptions.CAMPAIGN_TEMPLATE,
                'more_options',
              )
            }
            title="Campaign Templates"
          />
        )}
      </StyledMoreOptionsGrid>
    </StyledWrapper>
  );
}
